"use client"

import { useRef, useState, useCallback, useEffect, useMemo } from "react"
import { flushSync } from "react-dom"
import Image from "next/image"
import type { components } from "umbraco"
import { resolveLinkHref } from "umbraco"
import { getLocalizedUrl, getNonUmbracoUrl, replaceRichTextLinks } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import { Swiper, SwiperSlide } from "swiper/react"
import { Autoplay } from "swiper/modules"
import type { Swiper as SwiperType } from "swiper"
import "swiper/css"

import PauseIcon from "../../icons/PauseIcon"
import PlayIcon from "../../icons/PlayIcon"
import { useScrollScrub } from "../../transitions/useScrollScrub"
import type { NavBarState } from "./styles"
import {
  Root,
  SwiperWrapper,
  SlideMediaWrapper,
  DesktopMedia,
  MobileMedia,
  SlideVideo,
  Overlay,
  ContentLayer,
  MainContent,
  DecorativeImage,
  ContentContainer,
  TitleBlock,
  TitleTextWrapper,
  TitleText,
  DescriptionTextWrapper,
  DescriptionText,
  CtaButton,
  CtaLink,
  CtaShellLink,
  CtaShellAnchor,
  CtaLabelStack,
  CtaLabelLine,
  CarouselNavContainer,
  CarouselNavBars,
  NavBar,
  NavBarTrack,
  NavBarProgress,
  CircleIconButton,
} from "./styles"

type PromotionalBannerProps =
  components["schemas"]["PromotionalBannerBlockPropertiesModel"]
type PromotionalBannerSlideProps =
  components["schemas"]["PromotionalBannerSlideBlockPropertiesModel"]

interface Props {
  data: PromotionalBannerProps
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  [key: string]: unknown
}

const AUTOPLAY_DELAY = 5000

// EGDE-624: at 375px the nav bars crowd out the rest of the row past the sixth
// slide. The CMS still accepts up to 10, so the extras are dropped rather than
// rendered.
const MAX_SLIDES = 6

interface ResolvedMedia {
  url: string
  isVideo: boolean
  alt: string
}

interface SlideMedia {
  desktopUrl: string
  isVideo: boolean
  // EGDE-78: dedicated mobile background; null → reuse the desktop media.
  mobileUrl: string | null
  alt: string
}

function resolveMedia(field: unknown): ResolvedMedia | null {
  if (!Array.isArray(field) || field.length === 0) return null
  const media = field[0] as any
  const url = media?.url
  if (!url) return null

  const extension = (media?.extension ?? "").toLowerCase()
  const mediaType = (media?.mediaType ?? "").toLowerCase()
  // EGDE-566/571: Umbraco reports "umbracoMediaVideo" (with empty extension)
  // for uploaded videos — strict equality never matched (cf. EGDE-36).
  const isVideo =
    mediaType.includes("video") ||
    ["mp4", "webm", "ogg", "mov"].includes(extension)

  const absoluteUrl = getUmbracoMediaUrl(url)
  // Cap source size at 1920px to prevent Next.js choking on very large Umbraco images
  const optimizedUrl =
    !isVideo && absoluteUrl ? `${absoluteUrl}?width=1920` : absoluteUrl

  return {
    url: optimizedUrl,
    isVideo,
    alt: (media?.name as string) ?? "",
  }
}

function getSlideMedia(
  mediaFile: unknown,
  mediaFileMobile: unknown,
): SlideMedia | null {
  const desktop = resolveMedia(mediaFile)
  if (!desktop) return null

  const mobile = resolveMedia(mediaFileMobile)
  return {
    desktopUrl: desktop.url,
    isVideo: desktop.isVideo,
    mobileUrl: mobile?.url ?? null,
    alt: desktop.alt,
  }
}

/* EGDE-504 (QA 2026-07-29 + Raz 2026-08-27): "Swap the images in a circular
 * order and do not go back and forth, only right to left." Swiper animates
 * toward a target's physical position, so clicking an EARLIER indicator swept
 * the other way (and a 2-ahead click flashed the slide in between). The track
 * cannot move forward to reach an earlier slide without travelling through
 * every slide between, so instead the CONTENT is remapped: each physical
 * position renders `slides[(position + offset) % count]`, and a nav click
 * pins the target's content onto the NEXT physical position and calls
 * `slideNext()` — every jump is exactly one forward sweep. When the sweep
 * lands, the pin is folded into a plain rotation so autoplay continues
 * circularly from the target. Off-screen positions re-render in that fold,
 * which is invisible with one slide per view.
 */
interface SlideOrder {
  offset: number
  /** During a nav-click sweep: the physical position carrying the target. */
  pin: { position: number; content: number } | null
}

export default function PromotionalBanner({ data, locale }: Props) {
  const swiperRef = useRef<SwiperType | null>(null)
  const videoRefs = useRef<Map<number, HTMLVideoElement>>(new Map())
  const [activeIndex, setActiveIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(true)
  const activeIndexRef = useRef(activeIndex)
  const [slideOrder, setSlideOrder] = useState<SlideOrder>({
    offset: 0,
    pin: null,
  })
  // Read by Swiper event handlers that fire synchronously inside goToSlide,
  // before React state lands — kept in step by hand wherever it is written.
  const slideOrderRef = useRef(slideOrder)
  // The physical slide the swiper sits on — drives video play/pause, which
  // must follow the POSITION: while a pin is live two positions can briefly
  // render the same content, so a content index no longer names one video.
  const [activePosition, setActivePosition] = useState(0)
  const activePositionRef = useRef(0)

  // EGDE-318: the band's X outline + copy fade in as it scrolls into view
  // (always on — per the ticket only contentCard / logosStrip / powerNumbers
  // carry a CMS toggle). Root gets --scrub; ContentLayer consumes it.
  const rootRef = useRef<HTMLDivElement>(null)
  useScrollScrub(rootRef, {
    // EGDE-479: with the original 0.6→0.1 window the copy only finished
    // fading in once the band top neared the viewport top — the tall band
    // read as blank for most of its scroll-through. Reveal early: start as
    // soon as the band enters and be fully formed by the time its top
    // reaches 70% of the viewport, so the top-anchored copy is opaque the
    // moment it is geometrically visible.
    startVh: 0.95,
    endVh: 0.7,
    onProgress: useCallback((p: number) => {
      const root = rootRef.current
      if (!root) return
      root.style.setProperty("--scrub", String(p))
      // EGDE-479: the CTA sat clickable while the copy was still faded out —
      // keep the content layer click-through until it is at least half formed.
      root.style.setProperty("--content-clicks", p >= 0.5 ? "auto" : "none")
    }, []),
  })

  useEffect(() => {
    activeIndexRef.current = activeIndex
  }, [activeIndex])

  // EGDE-624: capping here covers the whole block — the carousel slides, the
  // media list, the active-slide copy, the nav bars and the autoplay/loop
  // decisions all read this list. Memoised because `slice` would otherwise hand
  // `slideMediaList` a fresh array on every render.
  const slideItems = data.slides?.items
  const slides = useMemo(
    () => (slideItems ?? []).slice(0, MAX_SLIDES),
    [slideItems],
  )
  const slideCount = slides.length

  const slideMediaList = useMemo(
    () =>
      slides.map((slide) => {
        const slideProps = slide.content.properties as
          | PromotionalBannerSlideProps
          | undefined
        return getSlideMedia(slideProps?.mediaFile, slideProps?.mediaFileMobile)
      }),
    [slides],
  )

  const activeSlideMedia = slideMediaList[activeIndex]
  const isActiveSlideVideo = activeSlideMedia?.isVideo ?? false

  // EGDE-581: the copy (title / description / CTA) lives ONLY on the slide.
  // EGDE-582 introduced the per-slide fields but kept the banner-level ones as a
  // fallback, which left editors with two places to fill in and no way to tell
  // which one a slide was actually using. The banner-level fields are gone from
  // the CMS element, so each slide is self-contained.
  //
  // EGDE-504 (Raz 2026-08-27): "Button: stays in its position without motion.
  // Only the texts within the button change if the client has entered
  // different text for the button." Every slide's CTA is resolved up front: a
  // uniform CTA renders the plain static button; differing CTAs render ONE
  // static shell whose label stack rolls inside it (CtaLabelStack in styles).
  const slideCtas = useMemo(
    () =>
      slides.map((slide) => {
        const props = slide.content.properties as
          | PromotionalBannerSlideProps
          | undefined
        const label = props?.ctaLabel
        // CTA link — centralised resolution for Content / Media / External
        const resolved = resolveLinkHref(props?.ctaLink, {
          getNonUmbracoUrl,
          getUmbracoMediaUrl,
        })
        if (!label || !resolved?.href) return null
        return {
          label,
          href: resolved.href,
          target: resolved.target,
          isInternal: resolved.isInternal ?? false,
        }
      }),
    [slides],
  )
  const firstCta = slideCtas.find(Boolean) ?? null
  const uniformCta =
    firstCta &&
    slideCtas.every(
      (cta) =>
        cta &&
        cta.href === firstCta.href &&
        cta.label === firstCta.label &&
        cta.target === firstCta.target,
    )
      ? firstCta
      : null
  const activeCta = slideCtas[activeIndex] ?? null
  // A slide with no CTA fades the shell out in place (it must not unmount —
  // that would shift nothing below it, but remounting restarts the label
  // roll); the last shown CTA keeps the shell's href and label meanwhile.
  const lastCtaRef = useRef(firstCta)
  useEffect(() => {
    if (activeCta) lastCtaRef.current = activeCta
  }, [activeCta])
  const shellCta = activeCta ?? lastCtaRef.current ?? firstCta
  // One line per DISTINCT label: slides sharing a label share the line, so
  // the roll only plays when the text actually changes.
  const ctaLabels = useMemo(() => {
    const seen: string[] = []
    for (const cta of slideCtas) {
      if (cta && !seen.includes(cta.label)) seen.push(cta.label)
    }
    return seen
  }, [slideCtas])
  // The shell's component must not change mid-run (a remount restarts the
  // label roll), so it is the client-side Link only when EVERY cta is
  // internal, a plain anchor otherwise.
  const rotatingInternal = slideCtas.every((cta) => !cta || cta.isInternal)

  // EGDE-504 point 4: the roll needs EVERY slide's copy in the DOM at once, not
  // just the active slide's — one line has to be leaving while the next
  // arrives. Only the title and the description roll: the CTA label is the same
  // on every slide in the design, and the ticket asks for the texts.
  const slideCopy = useMemo(
    () =>
      slides.map((slide, index) => {
        const props = slide.content.properties as
          | PromotionalBannerSlideProps
          | undefined
        return {
          key: slide.content.id ?? `slide-${index}`,
          // Carried explicitly: the rendered lists are filtered, so a list
          // position is no longer the slide's position.
          index,
          title: props?.title,
          descriptionMarkup: props?.description?.markup,
        }
      }),
    [slides],
  )
  // Only slides that actually carry the field get a line. An empty element would
  // still be a flex child of TitleBlock, so it would add the 16px column gap
  // that `main` did not have, and it would collapse the clip box to nothing on
  // that slide so the outgoing line had nowhere to roll.
  const titleLines = slideCopy.filter((s) => s.title)
  const descriptionLines = slideCopy.filter((s) => s.descriptionMarkup)
  // The band's ONE h2 (EGDE-726) is the first slide that HAS a title, not
  // slide 0 — a slide 0 with an empty title would otherwise put the heading on
  // an empty element and render every real title as a <p>.
  const headingKey = titleLines[0]?.key

  // Force progress bar remount when carousel loops
  const cycleRef = useRef(0)

  // EGDE-504: which slide's content a physical position currently renders.
  const contentAt = useCallback(
    (position: number, order: SlideOrder) => {
      if (slideCount === 0) return 0
      if (order.pin && order.pin.position === position) return order.pin.content
      return (position + order.offset) % slideCount
    },
    [slideCount],
  )

  const handleSlideChange = useCallback(
    (swiper: SwiperType) => {
      setActivePosition(swiper.realIndex)
      activePositionRef.current = swiper.realIndex
      // A pin's sweep starts AT its position, so a change that lands anywhere
      // else means the pinned sweep never reported its transition end (a
      // touch or resize can swallow it). Drop the stale pin — leaving it
      // would permanently hijack that position's content.
      const order = slideOrderRef.current
      if (order.pin && order.pin.position !== swiper.realIndex) {
        const dropped: SlideOrder = { offset: order.offset, pin: null }
        slideOrderRef.current = dropped
        setSlideOrder(dropped)
      }
      const newIndex = contentAt(swiper.realIndex, slideOrderRef.current)
      setActiveIndex((prev) => {
        if (newIndex === 0 && prev > 0) cycleRef.current += 1
        return newIndex
      })
    },
    [contentAt],
  )

  // EGDE-504: once a nav-click sweep lands, fold the pin into a rotation so
  // the following autoplay steps continue circularly from the target.
  const handleTransitionEnd = useCallback(
    (swiper: SwiperType) => {
      const order = slideOrderRef.current
      if (!order.pin || slideCount === 0) return
      const shown = contentAt(swiper.realIndex, order)
      const folded: SlideOrder = {
        offset:
          (((shown - swiper.realIndex) % slideCount) + slideCount) %
          slideCount,
        pin: null,
      }
      slideOrderRef.current = folded
      setSlideOrder(folded)
    },
    [contentAt, slideCount],
  )

  useEffect(() => {
    videoRefs.current.forEach((video, position) => {
      if (position === activePosition) {
        video.play().catch(() => {})
      } else {
        video.pause()
        video.currentTime = 0
      }
    })
  }, [activePosition])

  // EGDE-530: the nav bars double as slide buttons.
  // EGDE-504: any jump is ONE forward sweep — the target's content is pinned
  // onto the next physical position (flushed so it is painted before the
  // sweep reveals it) and the swiper simply steps forward.
  const goToSlide = useCallback(
    (index: number) => {
      const swiper = swiperRef.current
      if (!swiper || swiper.animating || slideCount < 2) return
      if (index === activeIndexRef.current) return
      const pinned: SlideOrder = {
        offset: slideOrderRef.current.offset,
        pin: {
          position: (swiper.realIndex + 1) % slideCount,
          content: index,
        },
      }
      slideOrderRef.current = pinned
      flushSync(() => setSlideOrder(pinned))
      swiper.slideNext()
    },
    [slideCount],
  )

  const toggleAutoplay = useCallback(() => {
    const swiper = swiperRef.current
    if (!swiper) return

    setIsPlaying((prev) => {
      // videoRefs is keyed by PHYSICAL position (see the slide render), so the
      // lookup must be too — the content index diverges from it after a jump.
      const currentVideo = videoRefs.current.get(activePositionRef.current)
      if (prev) {
        swiper.autoplay.stop()
        currentVideo?.pause()
      } else {
        swiper.autoplay.start()
        currentVideo?.play().catch(() => {})
      }
      return !prev
    })
  }, [])

  return (
    <Root ref={rootRef} data-block="promotional-banner">
      <SwiperWrapper>
        {/* EGDE-504 (r3): the slides used to cross-FADE, which painted both
            photographs on top of each other for the whole 800ms — QA's
            recording shows a muddy double exposure of two different scenes.
            Raz's prototype changes slides with a hard-edged horizontal
            reveal, so the band now uses Swiper's default slide effect: only
            one photograph is ever visible at a given pixel. The hero carousel
            ships the same slide effect (EGDE-497) but NOT the same screen
            direction — see the r6 note below. */}
        {/* EGDE-504 (r6): dir="ltr" pins the sweep's SCREEN direction. In the
            prototype (attachment 183085) every change — both transitions,
            measured — brings the new photograph in from the RIGHT edge and
            pushes the old one out LEFT. A Swiper inside the RTL page resolves
            slideNext to the opposite sweep (in from the left), which is
            exactly Raz's 2026-08-31 return: "image 2 returns to image 1 from
            left to right… should always be right to left". The carousel holds
            only photographs (the copy lives outside it in ContentLayer), so
            fixing its internal direction changes no reading order — and the
            r5 pin-then-slideNext machinery keeps working untouched, it just
            sweeps the other way. LTR locales already resolved slideNext to
            this direction, so they are unchanged. The HERO carousel is left
            alone: it still sweeps inline-forward (enter-from-left on the
            Hebrew site) — Raz's returns and the prototype govern THIS band
            only, so the divergence is deliberate, not an oversight.
            allowTouchMove is off for the same reason: a drag resolves to
            slidePrev/slideNext in the drag's own direction, so a single swipe
            could still produce the forbidden left-to-right sweep (and would
            bypass the pin contract). The prototype's interaction model is
            autoplay plus the nav bars, which both survive. */}
        <Swiper
          dir="ltr"
          allowTouchMove={false}
          modules={[Autoplay]}
          autoplay={{
            delay: AUTOPLAY_DELAY,
            disableOnInteraction: false,
          }}
          loop={slideCount > 1}
          speed={800}
          onSwiper={(swiper) => {
            swiperRef.current = swiper
          }}
          onSlideChange={handleSlideChange}
          onSlideChangeTransitionEnd={handleTransitionEnd}
        >
          {/* EGDE-504: physical positions are FIXED (keys) — which slide's
              content each one renders comes from the order mapping, so a
              nav-click sweep can put any slide next in line. Inner content is
              keyed by the slide it shows so React swaps it cleanly. */}
          {slides.map((_, position) => {
            const contentIndex = contentAt(position, slideOrder)
            const slide = slides[contentIndex]
            const media = slideMediaList[contentIndex]
            if (!media) {
              return (
                <SwiperSlide key={`position-${position}`}>
                  <SlideMediaWrapper />
                </SwiperSlide>
              )
            }

            // Desktop media — video or image (used both standalone and as the
            // ≥lg layer when a dedicated mobile image exists).
            const desktopMedia = media.isVideo ? (
              <SlideVideo
                ref={(el) => {
                  if (el) videoRefs.current.set(position, el)
                  else videoRefs.current.delete(position)
                }}
                src={media.desktopUrl}
                muted
                loop
                playsInline
                preload="auto"
              />
            ) : (
              <Image
                src={media.desktopUrl}
                alt={media.alt}
                fill
                sizes="100vw"
                style={{ objectFit: "cover" }}
                priority={position === 0 && contentIndex === 0}
              />
            )

            return (
              <SwiperSlide key={`position-${position}`}>
                <SlideMediaWrapper key={slide.content.id ?? contentIndex}>
                  {media.mobileUrl ? (
                    <>
                      <DesktopMedia>{desktopMedia}</DesktopMedia>
                      <MobileMedia>
                        <Image
                          src={media.mobileUrl}
                          alt={media.alt}
                          fill
                          sizes="100vw"
                          style={{ objectFit: "cover" }}
                          priority={position === 0 && contentIndex === 0}
                        />
                      </MobileMedia>
                    </>
                  ) : (
                    desktopMedia
                  )}
                </SlideMediaWrapper>
              </SwiperSlide>
            )
          })}
        </Swiper>
      </SwiperWrapper>

      <Overlay />

      <ContentLayer>
        <MainContent>
          {/*
           * Mobile (column): source order = visual order: X first, content second.
           * Desktop (RTL row): source order reversed — content first, X second
           * renders as X left, content right. We use CSS order to handle both.
           */}
          <ContentContainer>
            {slideCount > 1 && (
              <CarouselNavContainer>
                {/* RTL row: first child → RIGHT (start). Button on right, bars on left. */}
                <CircleIconButton
                  onClick={toggleAutoplay}
                  aria-label={isPlaying ? "Pause carousel" : "Play carousel"}
                >
                  {isPlaying ? <PauseIcon /> : <PlayIcon />}
                </CircleIconButton>

                <CarouselNavBars>
                  {slides.map((slide, index) => {
                    /* EGDE-504 (r6): earlier bars used to render "completed" —
                       a full white fill — so on slide 2 the slide-1 bar sat
                       solid while the active bar was still filling. Raz's
                       2026-08-31 return names exactly that ("pager of image 1
                       in fill when it is focused on image 2"), and the
                       prototype (183085) never shows it: only the ACTIVE bar
                       carries fill, every other bar stays the dim track. With
                       the r5 circular order there is no "earlier" slide
                       anyway. A paused active bar holds full so the current
                       slide stays marked without pretending to progress. */
                    const state: NavBarState =
                      index === activeIndex
                        ? isPlaying
                          ? "active"
                          : "held"
                        : "idle"
                    return (
                      <NavBar
                        key={slide.content.id ?? `nav-${index}`}
                        type="button"
                        onClick={() => goToSlide(index)}
                        aria-label={`Go to slide ${index + 1}`}
                        aria-current={index === activeIndex}
                      >
                        <NavBarTrack>
                          <NavBarProgress
                            key={`${cycleRef.current}-${activeIndex}-${isPlaying}-${index}`}
                            $state={state}
                            $duration={AUTOPLAY_DELAY}
                          />
                        </NavBarTrack>
                      </NavBar>
                    )
                  })}
                </CarouselNavBars>
              </CarouselNavContainer>
            )}

            {/* EGDE-504 point 4: the copy rolls between slides instead of
                popping, so every slide's title and description is rendered and
                the active one is chosen in CSS — see textRoll in styles.ts.
                The wrappers stay mounted whatever the active slide carries:
                they are grid stacks sized by their TALLEST line, so the CTA
                below never moves (Raz 2026-08-27). */}
            <TitleBlock>
              {titleLines.length > 0 && (
                <TitleTextWrapper>
                  {titleLines.map(({ key, index, title: slideTitle }) => (
                    <TitleText
                      key={key}
                      variant="h2"
                      // EGDE-726: the heading outline gets ONE h2 for the band.
                      // The other slides keep the h2 LOOK but render as <p> —
                      // stacking every slide's title would otherwise hand the
                      // page a run of headings only one of which is on screen.
                      // No `inert` here, unlike the description: it would pull
                      // that single h2 out of the a11y tree on every slide but
                      // one, which is worse than an off-screen heading. Titles
                      // hold no focusable content, so there is no tab order to
                      // protect. The hero does the same.
                      component={key === headingKey ? "h2" : "p"}
                      $active={index === activeIndex}
                    >
                      {slideTitle}
                    </TitleText>
                  ))}
                </TitleTextWrapper>
              )}
              {descriptionLines.length > 0 && (
                <DescriptionTextWrapper>
                  {descriptionLines.map(
                    ({ key, index, descriptionMarkup: markup }) => (
                      <DescriptionText
                        key={key}
                        $active={index === activeIndex}
                        // Rich text carries links (EGDE-542) — without `inert`
                        // the hidden slides' links stay tabbable.
                        inert={index !== activeIndex}
                        dangerouslySetInnerHTML={{
                          __html: replaceRichTextLinks(markup as string),
                        }}
                      />
                    ),
                  )}
                </DescriptionTextWrapper>
              )}
            </TitleBlock>

            {/* One CTA for the whole band: static, exactly as before. */}
            {uniformCta && uniformCta.isInternal && (
              <CtaLink
                href={uniformCta.href}
                target={uniformCta.target}
                rel={
                  uniformCta.target === "_blank"
                    ? "noopener noreferrer"
                    : undefined
                }
              >
                {uniformCta.label}
              </CtaLink>
            )}
            {uniformCta && !uniformCta.isInternal && (
              <CtaButton
                href={uniformCta.href}
                target={uniformCta.target}
                rel={
                  uniformCta.target === "_blank"
                    ? "noopener noreferrer"
                    : undefined
                }
              >
                {uniformCta.label}
              </CtaButton>
            )}
            {/* Differing CTAs: ONE stationary shell — its href follows the
                active slide, and only the label rolls (EGDE-504, Raz
                2026-08-27). */}
            {!uniformCta &&
              shellCta &&
              (() => {
                const Shell = rotatingInternal ? CtaShellLink : CtaShellAnchor
                return (
                  <Shell
                    // The plain-anchor shell (a mixed internal/external set)
                    // bypasses the localized Link, so an internal href must
                    // carry the locale prefix itself; external hrefs pass
                    // through getLocalizedUrl untouched.
                    href={
                      rotatingInternal
                        ? shellCta.href
                        : getLocalizedUrl(shellCta.href, locale)
                    }
                    target={shellCta.target}
                    rel={
                      shellCta.target === "_blank"
                        ? "noopener noreferrer"
                        : undefined
                    }
                    $hidden={!activeCta}
                  >
                    <CtaLabelStack>
                      {ctaLabels.map((label) => (
                        <CtaLabelLine
                          key={label}
                          $active={label === shellCta.label}
                        >
                          {label}
                        </CtaLabelLine>
                      ))}
                    </CtaLabelStack>
                  </Shell>
                )
              })()}
          </ContentContainer>

          <DecorativeImage
            src="/images/promotional-banner/decorative-x.png"
            alt=""
            loading="lazy"
          />
        </MainContent>
      </ContentLayer>
    </Root>
  )
}
